import { Dropdown, Icon, Menu, Popover, Button, Badge } from "antd";
import moment from "moment";
import React from "react";
import "moment/locale/pt-br";

import { ModuleHeader } from '~/components/ModuleHeader';
import MonthPicker from '~/_shared/components/MonthPicker';
import Search from '~/_shared/components/Search';
import FilterPillsBar from '~/_shared/components/FilterPillsBar';
import { useLeadsContext } from '../../context/LeadsContext';
import UserSelector from '../UserSelector';
import PeriodFilter from './PeriodFilter';
import { LEADS_FILTER_BY_OPTIONS } from '../../helpers/filters';

import { downloadExcel } from "../LeadsTable/downloadExcel";

/**
 * Leads header. The filters are owned by the page (persisted with
 * usePersistedFilters, see helpers/filters.js); this component renders them:
 *   - pills / filterCount: applied filters, computed by the page
 *   - onClearAll: resets every filter (undefined when nothing to clear)
 *   - period / onPeriodSubmit: table view range ({ from, to, filterBy })
 */
export const Header = ({
  displayMode,
  setDisplayMode,
  date,
  setDate,
  showDrawer,
  setIsMetricsModalVisible,
  selectedRowsKeys,
  selectedRows,
  columns,
  period,
  onPeriodSubmit,
  searchQuery,
  setSearchQuery,
  selectedUserId,
  onUserChange,
  pills,
  filterCount,
  onClearAll,
}) => {
  const { searchResultsCount } = useLeadsContext();

  return (
    <>
      <ModuleHeader
        breadcrumbs={['Leads']}
        search={
          <Search
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar leads"
          />
        }
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>Leads</span>
            {searchResultsCount !== null && (
              <span style={{
                fontSize: '12px',
                color: '#666',
                backgroundColor: '#f0f0f0',
                padding: '2px 8px',
                borderRadius: '12px'
              }}>
                {searchResultsCount} resultado{searchResultsCount !== 1 ? 's' : ''} encontrado{searchResultsCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        }
        center={
          <>
            <Popover
              trigger="click"
              placement="bottomRight"
              content={
                <div style={{ width: 420 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {onClearAll && (
                        <a
                          style={{ fontSize: 12, color: '#f5222d' }}
                          onClick={onClearAll}
                        >
                          Limpar tudo
                        </a>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Display mode */}
                    <div>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Visualização</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {[
                          { value: 'table', label: 'Tabela', color: '#1890ff', icon: 'table' },
                          { value: 'funnel', label: 'Funil', color: '#722ed1', icon: 'project' },
                          { value: 'calendar', label: 'Calendário', color: '#13c2c2', icon: 'calendar' },
                        ].map(function(opt) {
                          var isActive = displayMode === opt.value;
                          return (
                            <div
                              key={opt.value}
                              onClick={function() { setDisplayMode(opt.value); }}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                                border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'),
                                background: isActive ? opt.color + '0a' : '#fafafa',
                                transition: 'all 0.2s',
                              }}
                            >
                              <span style={{ fontSize: 12, color: isActive ? '#262626' : '#8c8c8c' }}>
                                <Icon type={opt.icon} style={{ marginRight: 4 }} />
                                {opt.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {/* Date navigation (only for funnel mode) */}
                    {displayMode === 'funnel' && (
                      <div>
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Período</div>
                        <MonthPicker
                          value={moment(date, "MM-YYYY").format("YYYY-MM")}
                          onChange={function(newDate) {
                            var formattedDate = moment(newDate, "YYYY-MM").format("MM-YYYY");
                            setDate(formattedDate);
                          }}
                          showTodayButton={false}
                          style={{ display: 'block', width: '100%' }}
                        />
                      </div>
                    )}
                    {/* User selector */}
                    <div>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Responsável</div>
                      <UserSelector
                        selectedUserId={selectedUserId}
                        onUserChange={onUserChange}
                        placeholder="Filtrar por usuário"
                      />
                    </div>
                  </div>
                </div>
              }
            >
              <Badge
                count={filterCount}
                offset={[-4, 0]}
                style={{ backgroundColor: '#722ed1' }}
              >
                <Button
                  icon="bars"
                  style={{ borderRadius: 8 }}
                >
                  Filtros
                </Button>
              </Badge>
            </Popover>
            <Dropdown
              trigger={["click"]}
              overlay={
                <Menu>
                  <Menu.Item key="new-lead" onClick={function() { showDrawer(); }}>
                    <Icon type="plus" /> Novo Lead
                  </Menu.Item>
                  <Menu.Item key="metrics" onClick={function() { setIsMetricsModalVisible(true); }}>
                    <Icon type="bar-chart" /> Cadastrar métricas
                  </Menu.Item>
                  <Menu.Divider />
                  {selectedRowsKeys.length > 0 && displayMode === 'table' && (
                    <Menu.Item key="excel" onClick={function() { downloadExcel({ selectedRows: selectedRows, columns: columns }); }}>
                      <Icon type="file-excel" /> Exportar selecionados
                    </Menu.Item>
                  )}
                  <Menu.Item key="extension">
                    <a href="https://chrome.google.com/webstore/detail/contlabs-para-whatsapp/ggpdkeoppcdhhoidbjimchgpbeagikmb?hl=en" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                      <Icon type="chrome" /> Baixar extensão
                    </a>
                  </Menu.Item>
                </Menu>
              }
            >
              <Button icon="plus" shape="circle" />
            </Dropdown>
          </>
        }
        advanced={
          React.createElement(FilterPillsBar, {
            pills: pills,
            onClearAll: onClearAll,
          })
        }
      />

    {displayMode === "table" && period && (
      <PeriodFilter
        style={{marginTop: '10px'}}
        from={period.from}
        to={period.to}
        filterBy={period.filterBy}
        filterByOptions={LEADS_FILTER_BY_OPTIONS}
        onSubmit={onPeriodSubmit}
      />
    )}


    </>
  );
};
